import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import apiService from "../../services/api.js";
import { Container, Typography, Box, Link, Button } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import CallforAction from "../../components/call_for_action/CallForActtion.js";
import InnerLayout from "../../layouts/InnerLayout.js";
import InnerBanner from "../../images/inner-banner.jpg";
import EventCard from "../../components/EventCard";
import DateRangeIcon from "@mui/icons-material/DateRange";
import LoadingIcon from "../../components/LoadingIcon";
import htmlConvert from "../../themeSettings/htmlConvert.js";

function EventDetails() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    apiService
      .getEventCategories()
      .then((response) => {
        if (response.data.success && response.data.data) {
          const foundEvent = response.data.data
            .flatMap((category) => category.events)
            .find((event) => event.slug === slug);

          if (foundEvent) {
            setEvent(foundEvent);
            setBreadcrumbs([
              { label: "Home", path: "/" },
              { label: "Events", path: "/events" },
              { label: foundEvent.title, path: `/events/${slug}` },
            ]);

            const related = response.data.data
              .flatMap((category) => category.events)
              .filter(
                (eventItem) =>
                  eventItem.category_name === foundEvent.category_name &&
                  eventItem.slug !== slug
              );

            // Sort related events: upcoming first (closest first), then ongoing, then past (most recent first)
            const now = new Date();
            related.sort((a, b) => {
              const startA = new Date(a.start_date_time);
              const startB = new Date(b.start_date_time);
              const endA = a.end_date_time ? new Date(a.end_date_time) : null;
              const endB = b.end_date_time ? new Date(b.end_date_time) : null;

              // Check if events are upcoming (in the future)
              const isUpcomingA = startA > now;
              const isUpcomingB = startB > now;
              
              // Check if events are ongoing
              const today = new Date(now);
              today.setHours(0, 0, 0, 0);
              const isOngoingA = !isUpcomingA && ((endA && endA >= now) || 
                              (!endA && new Date(startA).toDateString() === today.toDateString()));
              const isOngoingB = !isUpcomingB && ((endB && endB >= now) || 
                              (!endB && new Date(startB).toDateString() === today.toDateString()));

              // Upcoming events come first (closest first)
              if (isUpcomingA && !isUpcomingB) return -1;
              if (!isUpcomingA && isUpcomingB) return 1;
              if (isUpcomingA && isUpcomingB) return startA - startB;
              
              // Then ongoing events
              if (isOngoingA && !isOngoingB) return -1;
              if (!isOngoingA && isOngoingB) return 1;
              
              // Then past events (most recent first)
              return startB - startA;
            });

            setRelatedEvents(related);
          } else {
            setError("Event not found.");
          }
        } else {
          setError("Event categories could not be loaded.");
        }
        setLoading(false);
      })
      .catch((err) => {
        setError("Failed to load event details.");
        setLoading(false);
      });
  }, [slug]);

  // Handle scroll event
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100); // Adjust 100 as needed
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading) return <LoadingIcon />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <InnerLayout>
      <Box component="section" className="inner-banner">
        <Box
          component="img"
          className="banner-pic"
          src={InnerBanner}
          alt={event.title}
        />

        <Box className={`position-buttons ${isScrolled ? 'active' : ''}`}>
          <CallforAction/>
        </Box>

        <Box className="inner-text">
          <Container maxWidth="xl">
            <Grid
              container
              spacing={2}
              sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <Grid
                item
                size={{ xs: 12, md: 6 }}
                className="page-title-section"
              >
                {/* Dynamic Breadcrumbs */}
                <Breadcrumbs
                  aria-label="breadcrumb"
                  className="text-white breadcrumb"
                >
                  {breadcrumbs.map((breadcrumb, index) => (
                    <Link
                      key={index}
                      underline="hover"
                      color={
                        index === breadcrumbs.length - 1
                          ? "textWhite"
                          : "inherit"
                      }
                      component={RouterLink}
                      to={breadcrumb.path}
                      style={
                        index === breadcrumbs.length - 1
                          ? { color: "white" }
                          : {}
                      }
                    >
                      {breadcrumb.label}
                    </Link>
                  ))}
                </Breadcrumbs>

                <Typography
                  component="h1"
                  variant="h1"
                  className="section-title text-white"
                >
                  {event.title}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <CallforAction />
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>

      <Box component="section">
        <Container maxWidth="xl">
          <Grid
            container
            spacing={2}
            sx={{ justifyContent: "space-between", alignItems: "start" }}
          >
            <Grid item size={{ xs: 12, md: 7 }}>
              <Box component="img" src={event.image} className="w-100 mb-30" />

              <Typography
                variant="h2"
                className="text-primary event-details-title"
              >
                {event.title}
              </Typography>

              <Box className="detail-date">
                <DateRangeIcon />
                {new Date(event.start_date_time).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                {event.end_date_time && (
                  <>
                    {' - '}
                    {new Date(event.end_date_time).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                  </>
                )}
              </Box>

              <Box component="div" className="section-content">
                {event.short_description && htmlConvert({ htmlContent: event.short_description })}
              </Box>

              <Box component="div" className="section-content mt-30">
                {event.description && htmlConvert({ htmlContent: event.description })}
              </Box>
            </Grid>

            <Grid item size={{ xs: 12, md: 4 }}>
              <Box className="related-events">
                <Typography
                  variant="h5"
                  className="text-primary font-700"
                  gutterBottom
                >
                  Related Events
                </Typography>
                {relatedEvents.length > 0 ? (
                  <>
                    <Box spacing={2}>
                      {relatedEvents.slice(0, 4).map((relatedEvent, index) => (
                        <Box className="related-events-lists" md={4} key={index}>
                          <EventCard event={relatedEvent} />
                        </Box>
                      ))}
                    </Box>
                    {relatedEvents.length > 4 && (
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          component={RouterLink}
                          to="/events"
                          className="w-100"
                 
                        >
                          Show More
                        </Button>
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography>No related events found.</Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </InnerLayout>
  );
}

export default EventDetails;